<template>
  <v-container>
    <back-button :label="transPlural('object.object')" fallback-route="ObjectsPage"></back-button>

    <request-state :request-promise="objectRequest" :retry-action="getObject">
      <template v-if="object && object.id">
        <page-heading>{{ pageTitle }}</page-heading>

        <v-form>
          <v-card>
            <v-card-title>
              {{ trans('object.mainInformation') }}
            </v-card-title>

            <v-card-text>
              <form-input
                :label="trans('object.name')"
                v-model="object.name"
                readonly
                cols="3"
              ></form-input>

              <form-input
                :label="trans('object.address')"
                :value="fullAddress"
                readonly
              ></form-input>

              <form-input
                :label="trans('object.capacity')"
                readonly
              >
                <template slot="input">
                  <span>{{object.size}} m²</span><br>
                  <span>{{object.rooms}} {{transc('object.rooms', object.rooms)}}</span><br>
                  <span>{{object.maxGuests}} {{transc('object.maxGuests', object.maxGuests)}}</span>
                </template>
              </form-input>
            </v-card-text>
          </v-card>

          <template v-if="hasAnyPermission('OBJECT_VIEW_DETAILS')">

            <v-card class="mt-3">
              <v-card-title>
                {{ trans('object.settingsGeneral') }}
              </v-card-title>

              <v-card-text>
                <form-input
                  :label="trans('object.group')"
                  cols="6"
                >
                  <template v-slot:input>
                    <v-row class="flex-nowrap">
                      <select-group
                        :group-id="object.groupId"
                        :clearable="false"
                        @update:groupId="objectGroupId => object.groupId = objectGroupId"
                      />
                      <v-btn class="ml-6 warning--text" @click="object.groupId = null" :disabled="object.groupId == null">
                        <v-icon left>mdi-home-group-remove</v-icon>{{ trans('object.removeFromGroup') }}
                      </v-btn>
                    </v-row>
                    <router-link
                      v-if="object.groupId"
                      :to="{name: 'ObjectGroupPage', params: {objectGroupId: object.groupId}}"
                    >
                      <v-icon small left>mdi-arrow-right</v-icon>
                      <span class="caption">{{ trans('objectGroup.gotoObjectGroup') }}</span>
                    </router-link>
                  </template>
                </form-input>
                <form-input
                  :label="trans('object.status')"
                  v-model="object.status"
                  :help-text="trans('object.statusHelp')"
                  type="checkbox"
                >
                </form-input>
                <form-input
                  :label="trans('object.settlementNotes')"
                  :help-text="trans('object.settlementNotesHelp')"
                  v-model="object.settlementNotes"
                  :error-messages="validationErrors.settlementNotes"
                  type="textarea"
                  cols="6"
                  height="70"
                  optional
                ></form-input>
                <form-input
                    :label="trans('object.noAutoCheck')"
                    v-model="object.noAutoCheck"
                    :help-text="trans('object.noAutoCheckHelp')"
                    type="checkbox"
                />
                <form-input
                    :label="trans('object.requiresManualEmail')"
                    v-model="object.requiresManualEmail"
                    :help-text="trans('object.requiresManualEmailHelp')"
                    type="checkbox"
                />
                <form-input
                  :label="trans('object.sendSettlementWithZeroAmount')"
                  v-model="object.sendSettlementWithZeroAmount"
                  :help-text="trans('object.sendSettlementWithZeroAmountHelp')"
                  type="checkbox"
                />
                <form-input
                  cols="3"
                  v-if="canEditObjectInformation"
                >
                  <template v-slot:input>
                    <global-validation-errors :value="validationErrors" />
                    <save-button @click="updateObjectInformation"/>
                  </template>
                </form-input>
              </v-card-text>
              </v-card>

            <v-card class="mt-3" v-if="owner != null">
              <v-card-title>
                {{ trans('object.owner') }}
              </v-card-title>

              <v-card-text>
                <form-input
                  :label="trans('owner.name')"
                  v-model="owner.fullName"
                  readonly
                  cols="3"
                ></form-input>

                <form-input
                  :label="trans('owner.address')"
                  :value="owner.address"
                  type="textarea"
                  readonly
                  rows="3"
                ></form-input>

                <form-input
                  :label="trans('owner.email')"
                  v-model="owner.email"
                  readonly
                >
                </form-input>
                <form-input
                  :label="trans('object.ownerEmail.sendingEnabled')"
                  v-model="object.sendNewBookingMail"
                  :items="ownerEmailSettings"
                />
                <form-input
                  :label="trans('object.ownerEmail.attachContract')"
                  v-model="object.sendNewBookingAttach"
                  type="checkbox"
                />
                <form-input
                  :label="trans('owner.phoneNumber')"
                  v-model="owner.mobile"
                  readonly
                  cols="3"
                ></form-input>
                <form-input
                  :label="trans('owner.taxNumber')"
                  v-model="owner.taxId"
                  readonly
                  cols="3"
                ></form-input>
                <form-input
                  v-if="owner"
                  :label="trans('bankAccount.bankDetails')"
                  readonly
                >
                  <template slot="input">
                    <v-simple-table dense v-if="owner.bankAccount">
                      <tbody>
                      <tr v-for="field in ['bankName', 'iban', 'accountNumber', 'bic']" :key="field">
                        <td>{{ trans('bankAccount.' + field) }}</td>
                        <td>{{ owner.bankAccount[field] }}</td>
                      </tr>
                      </tbody>
                    </v-simple-table>
                  </template>
                </form-input>

                <form-input
                  :label="trans('owner.sepaMandateId')"
                  v-model="owner.sepaMandateId"
                  readonly
                  cols="3"
                >
                </form-input>

                <form-input
                  :label="trans('owner.sepaMandateSignedAt')"
                  v-model="owner.sepaMandateSign"
                  readonly
                  cols="3"
                  type="date"
                >
                </form-input>

                <form-input
                  cols="3"
                  v-if="canEditObjectInformation"
                >
                  <template v-slot:input>
                    <global-validation-errors :value="validationErrors" />
                    <save-button @click="updateObjectInformation"/>
                  </template>
                </form-input>
              </v-card-text>
            </v-card>

            <v-card class="mt-5">
              <v-card-title>{{ trans('consumption.consumptions') }}</v-card-title>
              <v-card-text>
                <form-input
                  v-for="(objectConsumption, i) in object.consumptions"
                  :key="'objectConsumption-' + i"
                  :label="trans('consumption.types.' + objectConsumption.type)"
                  :value="objectConsumption.amountPrice"
                  cols="2"
                  readonly
                >
                  <template #input>
                    <currency-amount
                      v-if="objectConsumption.amountPrice > 0"
                      :amount="objectConsumption.amountPrice"
                      :currency="'EUR / ' + trans('consumption.typeUnits.' + objectConsumption.type)"
                    />
                    <span v-else>&ndash;</span>
                  </template>
                </form-input>
              </v-card-text>
            </v-card>

          </template>
        </v-form>
      </template>
    </request-state>
  </v-container>
</template>

<script>
import PageHeading from '../components/PageHeading';
import {LocaleUtils} from '../utils/locale-utils';
import {MainUtils} from '../utils/main-utils';
import {RouterUtils} from '../utils/router-utils';
import {ObjectApi} from '@/api/object-api';
import FormInput from '@/components/FormInput';
import RequestState from '@/components/RequestState';
import BackButton from '@/components/BackButton';
import GlobalValidationErrors from "@/components/GlobalValidationErrors";
import SaveButton from "@/components/SaveButton";
import SelectGroup from "@/components/SelectGroup";
import CurrencyAmount from '@/components/CurrencyAmount.vue';


export default {
  name: 'ObjectPage',
  mixins: [
    LocaleUtils,
    MainUtils,
    RouterUtils,
  ],
  components: {
    CurrencyAmount,
    RequestState,
    PageHeading,
    FormInput,
    BackButton,
    GlobalValidationErrors,
    SaveButton,
    SelectGroup
  },
  props: ['objectId'],
  data: () => ({
    /** @type {Promise<EntityHttpResponse<ObjectDto>>} */
    objectRequest: null,
    /** @type {ObjectDto} */
    object: {
      groupId: null
    },
    validationErrors: {}
  }),

  computed: {
    objectName() {
      if(this.object) {
        return this.object.name;
      }
      return null;
    },
    fullAddress() {
      return this.object.street+', '+this.object.zipcode+' '+this.object.city+' ('+this.object.country+')';
    },
    ownerEmailSettings() {
      return [
        {value: 'DO_NOT_SEND', text: this.trans('object.ownerEmailSettings.doNotSend')},
        {value: 'SEND_WITHOUT_CONTRACT', text: this.trans('object.ownerEmailSettings.sendWithoutContract')},
        {value: 'SEND_WITH_CONTRACT', text: this.trans('object.ownerEmailSettings.sendWithContract')}
      ];
    },
    canEditObjectInformation() {
      return this.hasAnyPermission('OBJECT_EDIT');
    },
    pageTitle () {
      if (this.objectId) {
        return this.objectName;
      } else {
        return this.trans('object.new');
      }
    },
    /** @type {OwnerDto} */
    owner () {
      return (this.object && this.object.owner) ? this.object.owner : null;
    }
  },

  mounted() {
    if (this.objectId) {
      this.getObject();
    }
  },

  methods: {
    getObject() {
      this.objectRequest = ObjectApi.getById(this.objectId)
        .then(response => {
          response.data.groupId = response.data.group ? response.data.group.id : null;
          this.object = response.data;
        })
    },
    updateObjectInformation() {
      ObjectApi.updateObject(this.object.id, this.object)
        .then(response => {
          this.getObject();
          this.validationErrors = {};
          this.showSavedSuccessfullyNotification();
        })
        .catch(response => this.validationErrors = this.getValidationErrors(response));
    }
  }
}
</script>

<style>
</style>
